import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faCheckCircle, faExclamationTriangle, faEye, faEyeSlash, faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ReactTooltip from 'react-tooltip';
import BillingFrame from '../../components/BillingFrame';
import Copy2Clipboard from '../../components/Copy2Clipboard';
import Footer from '../../components/Footer';
import ProfileHeader from '../../components/ProfileHeader';
import { getEmailFromToken, getStoredAccessToken, getStoredRefershToken, renewRefreshToken, revokeRefreshToken, callSecuredAPI } from '../../utils/authUtils';
import './ProfilePage.scss';
import { getCode, python, shell } from './snippets';
import { BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import encrypt from '../../utils/encryptUrils';


const ProfilePage = () => {

  const [showToken, setShowToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [refreshToken, setRefreshToken] = useState(getStoredRefershToken());
  const userEmail = getEmailFromToken(getStoredAccessToken()) || '';
  const codeBlockTheme = dracula;
  const [usageHistory, setUsageHistory] = useState(null);
  const [currentUsage, setCurrentUsage] = useState(null);

  const onClickTokenCopy = () => {
    if (refreshToken) {
      setTokenCopied(true);
      navigator.clipboard.writeText(refreshToken);
      setTimeout(() => setTokenCopied(false), 500);
    }
  }

  const onClickRevoke = () => revokeRefreshToken();

  let iframeURL = refreshToken ? "https://widget.upgini.com/?provider=upgini_profile&api_key=" + encrypt(refreshToken) : "https://widget.upgini.com";

  const onClickRenewToken = async () => {
    let confirmMsg = '⚠️  Attention! This action will invalidate your current  API ' +
      'token and affect all applications using it. Are you sure to regenerate the API token?';
    if (window.confirm(confirmMsg)) {
      // renew token
      let newRefreshToken = await renewRefreshToken(getStoredRefershToken());
      setRefreshToken(newRefreshToken);
    }
  }

  const onClickCancelSubscription = async () => {
    let confirmMsg = "⚠️ Are you sure to cancel the transform subscription?";
    if (window.confirm(confirmMsg) && getStoredRefershToken()) {
      await callSecuredAPI({
        method: 'POST',
        url: window.Config.api.usage.cancel
      })
      let response = await callSecuredAPI({
        method: 'GET',
        url: window.Config.api.usage.current,
      })
      if (response.data) {
        setCurrentUsage(response.data);
      }
    }
  }

  const processData = (data) => {
    if (!data || !data.usages || data.usages.length === 0) {
      return { usages: [] };
    }

    const usages = data.usages;
    const result = [];

    // Сортировка массива по дате
    usages.sort((a, b) => new Date(a.day) - new Date(b.day));

    let earliestDate = new Date();
    earliestDate.setDate(earliestDate.getDate() - 30);
    for (let i = 0; i < usages.length - 1; i++) {
      let currentDate = new Date(usages[i].day);
      // Get only dates for the last month
      if (currentDate < earliestDate) {
        continue;
      }
      const nextDate = new Date(usages[i + 1].day);

      // Добавление текущего элемента
      result.push({
        day: currentDate.toISOString().split('T')[0],
        'transformed rows': usages[i].transformedRows
      });
      // Заполнение промежутков
      while ((currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1))) < nextDate) {
        result.push({
          day: currentDate.toISOString().split('T')[0],
          'transformed rows': 0
        });
      }
    }

    // Добавление последнего элемента
    result.push({
      day: new Date(usages[usages.length - 1].day).toISOString().split('T')[0],
      'transformed rows': usages[usages.length - 1].transformedRows
    });

    return { usages: result };
  };

  useEffect(() => {
    if (getStoredRefershToken()) {
      callSecuredAPI({
        method: 'GET',
        url: window.Config.api.usage.history
      }).then(response => {
        if (response.data?.usages) {
          const result = processData(response.data);
          setUsageHistory(result.usages);
        }
      })
    }
  }, []);

  useEffect(() => {
    if (getStoredRefershToken()) {
      callSecuredAPI({
        method: 'GET',
        url: window.Config.api.usage.current,
      }).then(response => {
        if (response.data) {
          setCurrentUsage(response.data);
        }
      })
    }
  }, []);

  // sandbox="allow-downloads allow-scripts allow-same-origin"
  // allow="clipboard-read; clipboard-write"

  // todo Replace ReactTooltip with this shortcut
  // const Tooltip = (props) => {
  //   return (
  //     <ReactTooltip id={props.id} place="top" type="dark" effect="solid">
  //       <span>
  //         {props.text}
  //       </span>
  //     </ReactTooltip>
  //   )
  // }

  // blur and disable if not authorized
  const containerStyle = getStoredRefershToken() ? { filter: '', pointerEvents: 'auto' } : { filter: 'blur(4px)', pointerEvents: 'none' };

  return <div className="profile-page-container" style={containerStyle}>

    <ProfileHeader />

    <div className="page-container">

      <div id="widget-block" className="page-block">
        <h2 id="widget" className="caption">
          To find new relevant data for your ML task upload your labeled dataset
        </h2>
        <div className="row-oriented-container wrapped">
          <iframe title="widget" src={iframeURL} frameBorder="0" width="742px" height="560px" allow="clipboard-read; clipboard-write"></iframe>
        </div>
      </div>

      <div id="support-block" className="page-block">
        <h2 id="support" className="caption">
          Support
        </h2>

        <div className="row-oriented-container wrapped">
          <a className="btn wide"
            target="_blank"
            rel="noopener noreferrer"
            href={`mailto:sales@upgini.com`}>
            Contact via Email
          </a>
        </div>
      </div>

      <div id="usage-block" className="page-block">
        <h2 id="usage" className="caption">
          Transform usage
        </h2>
        {usageHistory?.length > 0 &&
          <BarChart
            width={800}
            height={300}
            data={usageHistory}
          >
            <CartesianGrid strokeDasharray="2 2" />
            <XAxis dataKey="day" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="transformed rows" fill="rgba(2, 170, 217, 0.83)" activeBar={<Rectangle fill="rgba(2, 170, 217, 0.83)" stroke="blue" />} />
          </BarChart>
        }

        <div className="caption-annotation">
          {
            currentUsage?.hasLimit ? "You are using Trial plan. You have enriched " + currentUsage?.transformedRows + " of the " + currentUsage?.limit + " rows." : "You are using Premium plan."
          }
        </div>
        <br />
        <div className="row-oriented-container wrapped">
          {
            currentUsage?.hasLimit ? <a href="https://upgini.com/requet-a-quote" className="btn primary" target="_blank" rel="noopener noreferrer"><span>Request a quote</span></a> : <button className="btn primary" onClick={onClickCancelSubscription}>Cancel a subscription</button>
          }
        </div>
      </div>

      <div id="code-example-block" className="page-block">

        <h2 id="how-to" className="caption">
          <span role="img" aria-label="flag" className="imoji">🏁</span>
          How to use API key
        </h2>


        <div className="field-set">
          <span className="label"> Python </span>

          <SyntaxHighlighter className="code-block" language="python"
            data-tip data-for="snippet-python"
            onClick={() => setShowToken(getStoredRefershToken() && !showToken)}
            style={codeBlockTheme} showLineNumbers >
            {getCode(python, showToken, refreshToken)}
          </SyntaxHighlighter>
          {getStoredRefershToken() && <Copy2Clipboard text2Copy={getCode(python, true, refreshToken)} />}
          <ReactTooltip id="snippet-python" place="top" type="dark" effect="solid">
            <span>
              {showToken ? 'Click to hide token' : 'Click to show token'}
            </span>
          </ReactTooltip>
        </div>

        <div className="field-set">
          <span className="label"> Environment variable in bash </span>
          <SyntaxHighlighter className="code-block" language="bash"
            data-tip data-for="snippet-bash"
            onClick={() => setShowToken(getStoredRefershToken() && !showToken)}
            style={codeBlockTheme} showLineNumbers >
            {getCode(shell, showToken, refreshToken)}
          </SyntaxHighlighter>
          {getStoredRefershToken() && <Copy2Clipboard text2Copy={getCode(shell, true, refreshToken)} />}
          <ReactTooltip id="snippet-bash" place="top" type="dark" effect="solid">
            <span>
              {showToken ? 'Click to hide token' : 'Click to show token'}
            </span>
          </ReactTooltip>
        </div>

      </div>

      <div id="token-block" className="page-block">
        <h2 id="api-key" className="caption">
          <span role="img" aria-label="key" className="imoji">🔑</span>
          API key to access extra features and data sources
        </h2>

        <div className="caption-annotation">
          <a href="https://github.com/upgini/upgini#-open-up-all-capabilities-of-upgini">
            <span role="img" aria-label=""> 🏆 </span>
            <i> Benefits for registered users </i>
          </a>
        </div>

        <div id="email-section" className="field-set">
          <span className="label">Your email</span>
          <input id="email" value={userEmail} readOnly={true} />
        </div>

        <div id="copy-token-section" className="field-set">
          <span className="label">API key</span>

          <div className="row-oriented-container">

            <div className="row-oriented-container input-mimicry">
              <input id="tokeninput" type={showToken ? 'text' : 'password'} value={refreshToken} readOnly={true} />
              <div className="icon" data-tip data-for="show-hide-token-tooltip">
                <FontAwesomeIcon
                  onClick={() => setShowToken(getStoredRefershToken() && !showToken)}
                  icon={showToken ? faEye : faEyeSlash} />
              </div>
              <ReactTooltip id="show-hide-token-tooltip" place="top" type="dark" effect="solid">
                <span>
                  {showToken ? 'hide token' : 'show token'}
                </span>
              </ReactTooltip>
              <div className="icon" onClick={onClickTokenCopy} data-tip data-for="copy-token-tooltip">
                <FontAwesomeIcon icon={tokenCopied ? faCheckCircle : faCopy} />
              </div>
              <ReactTooltip id="copy-token-tooltip" place="top" type="dark" effect="solid">
                <span>
                  copy api key to clipboard
                </span>
              </ReactTooltip>
            </div>
            <button onClick={onClickRenewToken} className="btn primary additional-left-margin">
              <FontAwesomeIcon icon={faRedo} color="white" />
              &nbsp;
              Regenerate
            </button>

          </div>

        </div>

        <div id="try-it-section" className="field-set">
          <span className="label">Try it out</span>
          <div className="row-oriented-container wrapped">
            {
              getStoredRefershToken() &&
              <>
                <a className="btn wide right-margin"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://mybinder.org/v2/gh/upgini/upgini/main?urlpath=notebooks%2Fnotebooks%2Fkaggle_example.ipynb%3Fapi_key%3D%22${refreshToken}%22`}>
                  <img alt="mybinder" width="24px" height="24px" src="data:image/png;base64,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" />
                  Open example in Binder
                </a>
                <a className="btn wide left-margin"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://colab.research.google.com/github/upgini/upgini/blob/main/notebooks/kaggle_example.ipynb">
                  <svg viewBox="0 0 24 24" width="24px" height="24px" preserveAspectRatio="xMidYMid meet">
                    <g>
                      <path d="M4.54,9.46,2.19,7.1a6.93,6.93,0,0,0,0,9.79l2.36-2.36A3.59,3.59,0,0,1,4.54,9.46Z" fill="#e8710a"></path>
                      <path d="M2.19,7.1,4.54,9.46a3.59,3.59,0,0,1,5.08,0l1.71-2.93h0l-.1-.08h0A6.93,6.93,0,0,0,2.19,7.1Z" fill="#f9ab00"></path>
                      <path d="M11.34,17.46h0L9.62,14.54a3.59,3.59,0,0,1-5.08,0L2.19,16.9a6.93,6.93,0,0,0,9,.65l.11-.09" fill="#f9ab00"></path>
                      <path d="M12,7.1a6.93,6.93,0,0,0,0,9.79l2.36-2.36a3.59,3.59,0,1,1,5.08-5.08L21.81,7.1A6.93,6.93,0,0,0,12,7.1Z" fill="#f9ab00"></path>
                      <path d="M21.81,7.1,19.46,9.46a3.59,3.59,0,0,1-5.08,5.08L12,16.9A6.93,6.93,0,0,0,21.81,7.1Z" fill="#e8710a"></path>
                    </g>
                  </svg>
                  &nbsp;
                  Open example in Colab
                </a>
              </>
            }
          </div>
        </div>

        {
          false &&
          <div id="revoke-token-section" className="field-set">
            <span className="label">Revoke your API key</span>
            <div className="token-sub-section column-oriented-sub-section">
              <div className="red-revoke-notice-area">
                <div>
                  <FontAwesomeIcon className="red-revoke-notice-area-icon" icon={faExclamationTriangle} />
                  <span className="red-revoke-notice-area-label">
                    Attention: You'll lost your access and will be logged off from all devices
                  </span>
                </div>
                <button data-tip data-for='revokeButton'
                  onClick={onClickRevoke}
                  className="red-revoke-notice-area-button">
                  <div className="logout-icon" />
                  Revoke API key and logout
                </button>
              </div>
            </div>
          </div>
        }

      </div>

      {
        window.Config.showSubscription &&
        <div id="subscription-block" className="page-block">

          <h2 id="subscription" className="caption">
            <span role="img" aria-label="flag" className="imoji">💳</span>
            Subscription management
          </h2>

          <div className="billing-frame-container">
            {getStoredRefershToken() && <BillingFrame />}
          </div>

        </div>
      }

    </div>

    <Footer />

  </div >
}


export default ProfilePage;
